/**
 * Realtor contacts — the MLO's list behind the Realtor Marketing email: add / edit / delete,
 * bulk import from a CSV (paste or file, with column mapping and a preview), CSV export, and
 * opt-outs. An MLO only ever sees their own list; admins and super admins see every MLO's.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  CONTACT_COLUMN_LABELS,
  MAX_IMPORT_ROWS,
  US_STATES,
  guessContactMapping,
  mapContactRows,
  parseCsv,
  stateName,
  type ContactColumn,
  type ContactCsvMapping,
  type RealtorContactDto,
} from '@mana/shared';
import { api, apiDownload } from '../../lib/api.js';
import { Button } from '../../ui/Button.js';
import { fmtWhen } from './shared.js';

interface ListResponse {
  contacts: RealtorContactDto[];
  owners: Array<{ userId: string; name: string; email: string; count: number }>;
  admin: boolean;
}
const LIST_KEY = ['realtor-contacts'];
const SUMMARY_KEY = ['realtor-contacts-summary'];

const errText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);
const EMPTY_FORM = { fullName: '', email: '', phone: '', state: '', company: '', notes: '' };
type Form = typeof EMPTY_FORM;

export function RealtorContactsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');
  const [owner, setOwner] = useState('');
  const [showOptedOut, setShowOptedOut] = useState(true);
  const [editing, setEditing] = useState<{ id: string | null; form: Form } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const query = useQuery<ListResponse>({
    queryKey: [...LIST_KEY, state, owner],
    queryFn: () => api(`/api/realtor-contacts?${new URLSearchParams({ ...(state ? { state } : {}), ...(owner ? { userId: owner } : {}) }).toString()}`),
  });
  const contacts = query.data?.contacts ?? [];
  const admin = query.data?.admin ?? false;
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: LIST_KEY });
    void qc.invalidateQueries({ queryKey: SUMMARY_KEY });
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => (showOptedOut || !(c.optedOutAt || c.suppressed)) && (!q || `${c.fullName} ${c.email} ${c.company ?? ''} ${c.phone ?? ''}`.toLowerCase().includes(q)));
  }, [contacts, search, showOptedOut]);
  const statesInList = useMemo(() => [...new Set(contacts.map((c) => c.state))].sort(), [contacts]);
  const optedOutCount = contacts.filter((c) => c.optedOutAt || c.suppressed).length;

  const saveMut = useMutation({
    mutationFn: async (e: { id: string | null; form: Form; userId?: string }) =>
      e.id ? api<{ contact: RealtorContactDto }>(`/api/realtor-contacts/${e.id}`, { method: 'PUT', body: JSON.stringify(e.form) }) : api<{ contact: RealtorContactDto }>('/api/realtor-contacts', { method: 'POST', body: JSON.stringify({ ...e.form, ...(e.userId ? { userId: e.userId } : {}) }) }),
    onSuccess: (r) => {
      setEditing(null);
      setMsg({ tone: 'ok', text: `Saved ${r.contact.fullName}.` });
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Save failed') }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/api/realtor-contacts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setMsg({ tone: 'ok', text: 'Contact removed.' });
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Delete failed') }),
  });
  const optOutMut = useMutation({
    mutationFn: (a: { id: string; optedOut: boolean }) => api<{ contact: RealtorContactDto }>(`/api/realtor-contacts/${a.id}/opt-out`, { method: 'POST', body: JSON.stringify(a) }),
    onSuccess: (r) => {
      setMsg({ tone: 'ok', text: r.contact.optedOutAt ? `${r.contact.email} is opted out — it will be left out of every realtor email.` : `${r.contact.email} is re-subscribed.` });
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'err', text: errText(e, 'Update failed') }),
  });

  const exportCsv = async () => {
    try {
      await apiDownload(`/api/realtor-contacts/export.csv?${new URLSearchParams({ ...(state ? { state } : {}), ...(owner ? { userId: owner } : {}) }).toString()}`, 'realtor-contacts.csv');
    } catch (e) {
      setMsg({ tone: 'err', text: errText(e, 'Export failed') });
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-gold-600">
            <Link to="/mana-pricing" className="hover:underline">
              Mana Pricing
            </Link>{' '}
            · Realtor contacts
          </p>
          <h1 className="font-serif text-2xl font-bold text-brand-navy-800">Realtor contacts</h1>
          <p className="mt-1 max-w-2xl text-xs text-slate-500">
            The realtors who get your Realtor Marketing rate sheet, by state. {admin ? 'As an admin you see every loan officer’s list; each loan officer only sees their own.' : 'Only you see this list; other loan officers cannot.'} Anyone who unsubscribes is left out of every send automatically.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setImportOpen(true)}>
            Import CSV
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void exportCsv()} disabled={contacts.length === 0} title={contacts.length ? 'Download the list shown as a CSV (opens in Excel)' : 'Nothing to export yet'}>
            Export CSV
          </Button>
          <Button size="sm" onClick={() => setEditing({ id: null, form: EMPTY_FORM })}>
            Add contact
          </Button>
        </div>
      </div>

      {msg && (
        <p className={clsx('rounded-lg border px-3 py-2 text-xs', msg.tone === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700')} onClick={() => setMsg(null)}>
          {msg.text}
        </p>
      )}

      {editing && <ContactForm initial={editing.form} isNew={!editing.id} owners={admin ? (query.data?.owners ?? []) : []} pending={saveMut.isPending} onCancel={() => setEditing(null)} onSave={(form, userId) => saveMut.mutate({ id: editing.id, form, userId })} />}

      <div className="surface-card">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3 text-xs">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, company…" className="w-56 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-800 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200" />
          <select value={state} onChange={(e) => setState(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-800">
            <option value="">All states</option>
            {(statesInList.length ? statesInList : US_STATES.map((s) => s.code)).map((code) => (
              <option key={code} value={code}>
                {stateName(code)} ({code})
              </option>
            ))}
          </select>
          {admin && (
            <select value={owner} onChange={(e) => setOwner(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-800" title="Admins see every loan officer's contacts">
              <option value="">All loan officers</option>
              {(query.data?.owners ?? []).map((o) => (
                <option key={o.userId} value={o.userId}>
                  {o.name} ({o.count})
                </option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-1 text-slate-600">
            <input type="checkbox" checked={showOptedOut} onChange={(e) => setShowOptedOut(e.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300 accent-brand-navy-600" />
            Show opted out{optedOutCount ? ` (${optedOutCount})` : ''}
          </label>
          <span className="ml-auto text-slate-500">
            {visible.length} of {contacts.length} contact{contacts.length === 1 ? '' : 's'}
          </span>
        </div>
        {query.isLoading ? (
          <p className="px-4 py-6 text-xs text-slate-500">Loading…</p>
        ) : contacts.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-slate-500">
            <p className="text-sm font-medium text-slate-700">No realtor contacts yet.</p>
            <p className="mt-1">Add them one at a time, or import a CSV exported from your CRM or a spreadsheet (Excel → Save as CSV).</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-xs">
              <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-2 py-2">Email</th>
                  <th className="px-2 py-2">Phone</th>
                  <th className="px-2 py-2">State</th>
                  <th className="px-2 py-2">Company</th>
                  {admin && <th className="px-2 py-2">Loan officer</th>}
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const out = !!(c.optedOutAt || c.suppressed);
                  return (
                    <tr key={c.id} className={clsx('border-t border-slate-100', out && 'text-slate-400')}>
                      <td className="px-4 py-1.5 font-medium text-slate-800">
                        {c.fullName}
                        {c.notes && <span className="block text-[10px] font-normal text-slate-400">{c.notes}</span>}
                      </td>
                      <td className="px-2 py-1.5">{c.email}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{c.phone ?? '—'}</td>
                      <td className="px-2 py-1.5" title={stateName(c.state)}>
                        {c.state}
                      </td>
                      <td className="px-2 py-1.5">{c.company ?? '—'}</td>
                      {admin && <td className="px-2 py-1.5">{c.ownerName ?? '—'}</td>}
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {out ? (
                          <span className="rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700" title={c.optedOutAt ? `Opted out ${fmtWhen(c.optedOutAt)}` : 'Unsubscribed through another loan officer’s email (company-wide)'}>
                            Opted out{c.optedOutAt ? ` · ${fmtWhen(c.optedOutAt)}` : ''}
                          </span>
                        ) : (
                          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">Active</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <button type="button" className="text-brand-navy-600 hover:underline" onClick={() => setEditing({ id: c.id, form: { fullName: c.fullName, email: c.email, phone: c.phone ?? '', state: c.state, company: c.company ?? '', notes: c.notes ?? '' } })}>
                          Edit
                        </button>
                        {out ? (
                          admin && (
                            <button
                              type="button"
                              className="ml-2 text-brand-navy-600 hover:underline"
                              title="Only on the realtor's own request — clears the opt-out company-wide"
                              onClick={() => {
                                if (window.confirm(`Re-subscribe ${c.email}? Do this only when the realtor asked to be added back.`)) optOutMut.mutate({ id: c.id, optedOut: false });
                              }}
                            >
                              Re-subscribe
                            </button>
                          )
                        ) : (
                          <button type="button" className="ml-2 text-amber-700 hover:underline" title="Stop emailing this address (company-wide)" onClick={() => optOutMut.mutate({ id: c.id, optedOut: true })}>
                            Opt out
                          </button>
                        )}
                        <button
                          type="button"
                          className="ml-2 text-rose-600 hover:underline"
                          onClick={() => {
                            if (window.confirm(`Remove ${c.fullName} (${c.email}) from the list?`)) deleteMut.mutate(c.id);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {importOpen && (
        <ImportDialog
          admin={admin}
          owners={query.data?.owners ?? []}
          onClose={() => setImportOpen(false)}
          onDone={(text) => {
            setImportOpen(false);
            setMsg({ tone: 'ok', text });
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function ContactForm({ initial, isNew, owners, pending, onCancel, onSave }: { initial: Form; isNew: boolean; owners: Array<{ userId: string; name: string }>; pending: boolean; onCancel: () => void; onSave: (form: Form, userId?: string) => void }) {
  const [form, setForm] = useState<Form>(initial);
  const [userId, setUserId] = useState('');
  const field = (k: keyof Form, placeholder: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={placeholder} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-brand-navy-500 focus:outline-none focus:ring-2 focus:ring-brand-navy-200" {...extra} />
  );
  return (
    <form
      className="surface-card px-4 py-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form, userId || undefined);
      }}
    >
      <p className="mb-2 text-xs font-semibold text-brand-navy-800">{isNew ? 'New contact' : 'Edit contact'}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-6">
        {field('fullName', 'Full name *', { required: true })}
        {field('email', 'Email *', { type: 'email', required: true })}
        {field('phone', 'Phone')}
        <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} required className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900">
          <option value="">State *</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name} ({s.code})
            </option>
          ))}
        </select>
        {field('company', 'Company / brokerage')}
        {field('notes', 'Notes')}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {isNew && owners.length > 0 && (
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-800" title="Admins can add a contact to another loan officer's list">
            <option value="">My list</option>
            {owners.map((o) => (
              <option key={o.userId} value={o.userId}>
                {o.name}’s list
              </option>
            ))}
          </select>
        )}
        <Button size="sm" type="submit" disabled={pending}>
          {pending ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

const MAPPABLE: ContactColumn[] = ['fullName', 'firstName', 'lastName', 'email', 'phone', 'state', 'company', 'notes'];

function ImportDialog({ admin, owners, onClose, onDone }: { admin: boolean; owners: Array<{ userId: string; name: string }>; onClose: () => void; onDone: (text: string) => void }) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [mapping, setMapping] = useState<ContactCsvMapping | null>(null);
  const [mode, setMode] = useState<'upsert' | 'skip_existing'>('upsert');
  const [userId, setUserId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const rows = useMemo(() => (text.trim() ? parseCsv(text) : []), [text]);
  useEffect(() => {
    setMapping(rows.length ? guessContactMapping(rows) : null);
  }, [rows]);
  const mapped = useMemo(() => (rows.length && mapping ? mapContactRows(rows, mapping) : []), [rows, mapping]);
  const good = mapped.filter((r) => r.errors.length === 0);
  const bad = mapped.filter((r) => r.errors.length > 0);
  const headerCells = mapping?.hasHeader ? (rows[0] ?? []) : (rows[0] ?? []).map((_, i) => `Column ${i + 1}`);

  const importMut = useMutation({
    mutationFn: () =>
      api<{ created: number; updated: number; skipped: number; optedOut: number; total: number }>('/api/realtor-contacts/import', {
        method: 'POST',
        body: JSON.stringify({ rows: good.slice(0, MAX_IMPORT_ROWS).map((r) => r.input), mode, ...(userId ? { userId } : {}) }),
      }),
    onSuccess: (r) => onDone(`Imported ${r.total} contact${r.total === 1 ? '' : 's'}: ${r.created} added, ${r.updated} updated, ${r.skipped} left as they were${r.optedOut ? `, ${r.optedOut} had opted out and stay excluded` : ''}${bad.length ? `. ${bad.length} row${bad.length === 1 ? '' : 's'} skipped for errors.` : '.'}`),
    onError: (e) => setErr(errText(e, 'Import failed')),
  });

  const readFile = (f: File) => {
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result ?? ''));
    reader.readAsText(f);
  };
  const setColumn = (col: ContactColumn, idx: number) =>
    setMapping((m) => {
      if (!m) return m;
      const columns = { ...m.columns };
      for (const k of Object.keys(columns) as ContactColumn[]) if (columns[k] === idx) delete columns[k];
      if (idx >= 0) columns[col] = idx;
      else delete columns[col];
      return { ...m, columns };
    });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" className="surface-card my-6 w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-brand-navy-800">Import realtor contacts</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              A CSV with a name, email, phone and state per row (Excel or Google Sheets → Save as CSV). Columns are matched by their headers; fix any mapping below.{' '}
              <a href="#" className="text-brand-navy-600 hover:underline" onClick={(e) => { e.preventDefault(); void apiDownload('/api/realtor-contacts/template.csv', 'realtor-contacts-template.csv'); }}>
                Download a template
              </a>
              .
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="space-y-3 px-5 py-4">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <input ref={fileRef} type="file" accept=".csv,.txt,text/csv" className="hidden" onChange={(e) => e.target.files?.[0] && readFile(e.target.files[0])} />
            <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()}>
              Choose CSV file
            </Button>
            {fileName && <span className="text-slate-500">{fileName}</span>}
            <span className="text-slate-400">or paste below</span>
          </div>
          <textarea value={text} onChange={(e) => { setFileName(null); setText(e.target.value); }} rows={5} placeholder={'Full name,Email,Phone,State,Company\nJane Realtor,jane@example.com,(214) 555-0100,TX,Example Realty'} className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-xs text-slate-900 focus:border-brand-navy-500 focus:outline-none focus:ring-2 focus:ring-brand-navy-200" />

          {rows.length > 0 && mapping && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <label className="flex items-center gap-1 text-slate-600">
                  <input type="checkbox" checked={mapping.hasHeader} onChange={(e) => setMapping({ ...mapping, hasHeader: e.target.checked })} className="h-3.5 w-3.5 rounded border-slate-300 accent-brand-navy-600" />
                  First row is a header
                </label>
                {MAPPABLE.map((col) => (
                  <label key={col} className="flex items-center gap-1 text-slate-600">
                    <span>{CONTACT_COLUMN_LABELS[col]}:</span>
                    <select value={mapping.columns[col] ?? -1} onChange={(e) => setColumn(col, Number(e.target.value))} className="rounded border border-slate-300 px-1 py-0.5 text-xs text-slate-800">
                      <option value={-1}>—</option>
                      {headerCells.map((h, i) => (
                        <option key={i} value={i}>
                          {h || `Column ${i + 1}`}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full min-w-[640px] text-xs">
                  <thead className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-2 py-1">Line</th>
                      <th className="px-2 py-1">Name</th>
                      <th className="px-2 py-1">Email</th>
                      <th className="px-2 py-1">Phone</th>
                      <th className="px-2 py-1">State</th>
                      <th className="px-2 py-1">Company</th>
                      <th className="px-2 py-1">Check</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mapped.slice(0, 8).map((r) => (
                      <tr key={r.line} className={clsx('border-t border-slate-100', r.errors.length && 'bg-rose-50/60')}>
                        <td className="px-2 py-1 text-slate-400">{r.line}</td>
                        <td className="px-2 py-1">{r.input.fullName || '—'}</td>
                        <td className="px-2 py-1">{r.input.email || '—'}</td>
                        <td className="px-2 py-1">{r.input.phone || '—'}</td>
                        <td className="px-2 py-1">{r.input.state || '—'}</td>
                        <td className="px-2 py-1">{r.input.company || '—'}</td>
                        <td className="px-2 py-1">{r.errors.length ? <span className="text-rose-700">{r.errors.join(', ')}</span> : <span className="text-emerald-700">ok</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {mapped.length > 8 && <p className="px-2 py-1 text-[11px] text-slate-400">… and {mapped.length - 8} more rows</p>}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="font-medium text-slate-700">
                  {good.length} ready{bad.length ? `, ${bad.length} with problems (skipped)` : ''}
                  {good.length > MAX_IMPORT_ROWS ? ` — only the first ${MAX_IMPORT_ROWS} are imported per run` : ''}
                </span>
                <label className="flex items-center gap-1 text-slate-600">
                  <input type="checkbox" checked={mode === 'upsert'} onChange={(e) => setMode(e.target.checked ? 'upsert' : 'skip_existing')} className="h-3.5 w-3.5 rounded border-slate-300 accent-brand-navy-600" />
                  Update contacts already on the list with the file's details
                </label>
                {admin && owners.length > 0 && (
                  <select value={userId} onChange={(e) => setUserId(e.target.value)} className="rounded border border-slate-300 px-1 py-0.5 text-xs text-slate-800" title="Admins can import into another loan officer's list">
                    <option value="">Into my list</option>
                    {owners.map((o) => (
                      <option key={o.userId} value={o.userId}>
                        Into {o.name}’s list
                      </option>
                    ))}
                  </select>
                )}
                <Button size="sm" disabled={good.length === 0 || importMut.isPending} onClick={() => importMut.mutate()}>
                  {importMut.isPending ? 'Importing…' : `Import ${Math.min(good.length, MAX_IMPORT_ROWS)} contact${good.length === 1 ? '' : 's'}`}
                </Button>
              </div>
            </>
          )}
          {err && <p className="text-xs text-rose-700">{err}</p>}
        </div>
      </div>
    </div>
  );
}
